// @ts-nocheck
import { FC, useState, useEffect } from "react";
import {
  Drawer,
  Box,
  FormControl,
  useTheme,
  useMediaQuery,
  IconButton,
  Snackbar,
  SnackbarContent,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useLanguage } from "../LanguageContext";
import { Language, openDrawerProps, CategoryData } from "../../Types/types";
import FilterHeader from "../Headers/FilterHeader";
import FilterDrawer from "./FilterDrawer";
import translationOpenDrawer from "./translationOpenDrawer";
import Button from "../Button";
import Input from "../Input";
import { SelectChangeEvent } from "@mui/material/Select";

const SelectedCategory: FC<{ categoryLabel: string; onDelete: () => void }> = ({
  categoryLabel,
  onDelete,
}) => (
  <Box display="flex" alignItems="center">
    <Box
      p={1}
      borderRadius="4px"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="auto"
    >
      <span style={{ fontSize: "0.875rem", marginRight: "8px" }}>
        {categoryLabel}
      </span>
      <button size="small" onClick={onDelete} className="text-white">
        <CloseIcon fontSize="small" />
      </button>
    </Box>
  </Box>
);

const OpenDrawer: FC<openDrawerProps> = ({
  open,
  onClose,
  searchTerm: initialSearchTerm,
  selectedCategories: initialSelectedCategories,
  onCategorySelect,
  categoryData = [],
  onOrderChange,
  order: initialOrder,
  onSearchClick,
  onClearClick,
  selectedSubCategories: initialSelectedSubCategories,
  onSubCategorySelect,
  subCategoryData = [],
  onSearchTermChange,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { language } = useLanguage();
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [subCategoryDrawerOpen, setSubCategoryDrawerOpen] = useState(false);
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    CategoryData[]
  >([]);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [selectedCategories, setSelectedCategories] = useState(
    initialSelectedCategories
  );
  const [selectedSubCategories, setSelectedSubCategories] = useState(
    initialSelectedSubCategories
  );
  const [order, setOrder] = useState<string | undefined>(initialOrder || "ASC");
  const t = translationOpenDrawer[language as Language];
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    setSearchTerm(initialSearchTerm);
    setSelectedCategories(initialSelectedCategories);
    setSelectedSubCategories(initialSelectedSubCategories);
    setOrder(initialOrder || "ASC");
  }, [
    initialSearchTerm,
    initialSelectedCategories,
    initialSelectedSubCategories,
    initialOrder,
  ]);

  const toggleCategoryDrawer = (open: boolean) => () => {
    setCategoryDrawerOpen(open);
  };

  const toggleSubCategoryDrawer = (open: boolean) => () => {
    setSubCategoryDrawerOpen(open);
  };

  const handleOrderToggle = () => {
    const newOrder = order === "ASC" ? "DESC" : "ASC";
    setOrder(newOrder);

    const syntheticEvent = {
      target: { value: newOrder },
    } as SelectChangeEvent<string>;

    onOrderChange(syntheticEvent);
  };

  const handleSearchClick = () => {
    onSearchClick();
  };

  const handleSubCategoryButtonClick = () => {
    if (selectedCategories.length === 0) {
      setShowSnackbar(true);
      return;
    }

    const selectedCategoryName = categoryData.find(
      (category) =>
        category.id.toString() ===
        selectedCategories[selectedCategories.length - 1]
    )?.name;

    if (selectedCategoryName) {
      const normalizedCategoryName = selectedCategoryName
        .normalize("NFC")
        .toLowerCase()
        .trim();
      const matchingSubCategories = subCategoryData
        .filter(
          (subCategory) =>
            subCategory.category.normalize("NFC").toLowerCase().trim() ===
            normalizedCategoryName
        )
        .map((subCategory) => ({
          id: subCategory.id,
          name: subCategory.name,
          label: subCategory.name,
          order: 0,
          category: subCategory.category,
        }));
      setFilteredSubCategories(matchingSubCategories);
      setSubCategoryDrawerOpen(true);
    }
  };

  const selectedCategoryLabels = selectedCategories.map((categoryId) => {
    const category = categoryData.find(
      (cat) => cat.id.toString() === categoryId
    );
    return category ? category.name : "";
  });

  const selectedSubCategoryLabels = selectedSubCategories.map(
    (subCategoryId) => {
      const subCategory = subCategoryData.find(
        (subCat) => subCat.id.toString() === subCategoryId
      );
      return subCategory ? subCategory.name : "";
    }
  );

  const handleCategoryDelete = (categoryId: string) => {
    const updatedCategories = selectedCategories.filter(
      (id) => id !== categoryId
    );
    setSelectedCategories(updatedCategories);
    onCategorySelect(updatedCategories);

    if (updatedCategories.length === 0) {
      setSelectedSubCategories([]);
      onSubCategorySelect([]);
    }
  };

  const handleSubCategoryDelete = (subCategoryId: string) => {
    const updatedSubCategories = selectedSubCategories.filter(
      (id) => id !== subCategoryId
    );
    setSelectedSubCategories(updatedSubCategories);
    onSubCategorySelect(updatedSubCategories);
  };

  const handleSearchTermChange = (e: any) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    onSearchTermChange(newSearchTerm);
  };

  return (
    <div>
      <Drawer
        className="font-sans"
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: isSmallScreen ? "100%" : 384,
            minWidth: 320,
            display: "flex",
            flexDirection: "column",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(88, 110, 223, .8)",
          },
        }}
      >
        <div className="w-full h-screen flex flex-col justify-between bg-white dark:bg-darkBackground">
          <FilterHeader onClose={onClose} />
          <Box p={2} flex="1">
            <Box position="relative" mb={2}>
              <input
                className="w-full p-2 border border-purple rounded-md bg-white dark:bg-transparent dark:text-white Stext-black"
                placeholder={t.search}
                value={searchTerm}
                onChange={handleSearchTermChange}
              />
              <SearchIcon
                className="text-black dark:text-white"
                sx={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Box>

            <div>
              <FormControl fullWidth>
                <button
                  onClick={toggleCategoryDrawer(true)}
                  className="bg-white mt-2 rounded-md shadow p-2 dark:bg-darkSecondaryBackground dark:text-white"
                >
                  {t.categories}
                </button>
                {selectedCategories.length > 0 && (
                  <div className="bg-purple w-auto mr-auto rounded text-white mt-2">
                    {selectedCategoryLabels.map((label, index) =>
                      label ? (
                        <SelectedCategory
                          key={selectedCategories[index]}
                          categoryLabel={label}
                          onDelete={() =>
                            handleCategoryDelete(selectedCategories[index])
                          }
                        />
                      ) : null
                    )}
                  </div>
                )}
                <button
                  onClick={handleSubCategoryButtonClick}
                  className="bg-white mt-2 rounded-md shadow p-2 dark:bg-darkSecondaryBackground dark:text-white"
                >
                  {t.subCategories}
                </button>
                {selectedSubCategories.length > 0 && (
                  <div className="bg-purple w-auto mr-auto rounded text-white mt-2">
                    {selectedSubCategoryLabels.map((label, index) =>
                      label ? (
                        <SelectedCategory
                          key={selectedSubCategories[index]}
                          categoryLabel={label}
                          onDelete={() =>
                            handleSubCategoryDelete(
                              selectedSubCategories[index]
                            )
                          }
                        />
                      ) : null
                    )}
                  </div>
                )}
                <button
                  onClick={handleOrderToggle}
                  className="bg-white mt-2 rounded-md shadow p-2 dark:bg-darkSecondaryBackground dark:text-white"
                >
                  {order === "ASC"
                    ? `${t.order} ${t["ascending A-Z"]}`
                    : `${t.order} ${t["descending Z-A"]}`}
                </button>
              </FormControl>
            </div>
          </Box>

          <Box p={2} display="flex" flexDirection="column" gap={2}>
            <Button
              buttonText={t.search}
              onClick={handleSearchClick}
              className="bg-darkPurple hover:bg-purple text-white font-semibold -mb-2"
            />
            <Button
              buttonText={t.clear}
              onClick={onClearClick}
              className="bg-lightPurple hover:opacity-70 text-black font-semibold mb-2"
            />
          </Box>
        </div>
      </Drawer>

      <FilterDrawer
        open={categoryDrawerOpen}
        onClose={toggleCategoryDrawer(false)}
        categories={categoryData.map((category) => ({
          id: category.id,
          name: category.name,
          label: category.label,
          order: category.order,
          category: category.category,
        }))}
        subCategories={[]}
        selectedCategories={selectedCategories}
        selectedSubCategories={selectedSubCategories}
        onCategorySelect={(updatedCategories) => {
          setSelectedCategories(updatedCategories);
          onCategorySelect(updatedCategories);

          if (updatedCategories.length === 0) {
            setSelectedSubCategories([]);
            onSubCategorySelect([]);
          }
        }}
        onSubCategorySelect={(updatedSubCategories) => {
          setSelectedSubCategories(updatedSubCategories);
          onSubCategorySelect(updatedSubCategories);
        }}
        isSubCategoryDrawer={false}
      />

      <FilterDrawer
        open={subCategoryDrawerOpen}
        onClose={toggleSubCategoryDrawer(false)}
        categories={[]}
        subCategories={filteredSubCategories}
        selectedCategories={selectedCategories}
        selectedSubCategories={selectedSubCategories}
        onCategorySelect={(updatedCategories) => {
          setSelectedCategories(updatedCategories);
          onCategorySelect(updatedCategories);
        }}
        onSubCategorySelect={(updatedSubCategories) => {
          setSelectedSubCategories(updatedSubCategories);
          onSubCategorySelect(updatedSubCategories);
        }}
        isSubCategoryDrawer={true}
      />
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <SnackbarContent
          message={t.selectCategory}
          sx={{ backgroundColor: "orange", color: "white" }}
        />
      </Snackbar>
    </div>
  );
};

export default OpenDrawer;
